<template>
  <div class="jps-top">
    <ClientOnly>
      <section class="jps-top-slide-container" aria-roledescription="カルーセル">
        <div class="jps-top-slide-content">
          <CarouselVisual
            v-if="imageURLs.length > 0"
            :images="imageURLs"
            :to="currentImageElement?.internalLink"
            @change="zbgIndex = $event"
          />
        </div>
        <transition name="slow">
          <div class="top-slide-title-layout top-slide-title-position top-slide-title-styled"
            v-if="currentImageElement"
            :key="currentImageElement.key"
            ref="target-slide"
            aria-live="polite"
            aria-roledescription="スライド"
          >
            <BaseLink :to="currentImageElement.internalLink || currentImageElement.link">
              <h1 class="top-image-title is-size-5-desktop is-size-6-touch">
                {{ ls(currentImageElement.title) }}
              </h1>
            </BaseLink>
            <p class="top-image-description is-size-7">
              {{ ls(currentImageElement.description) }}
            </p>
            <div v-if="currentImageDb" class="top-image-source is-size-7">
              <UiDbSpan :id="currentImageDb" :nolink="false" />
            </div>
          </div>
        </transition>
      </section>
    </ClientOnly>
  </div>

  <BaseContainer
    class="hina-search"
    inner-class="hina-top-section"
  >
    <h1 class="hina-search-heading">
      <LocaleLogoHina
        layout="left-stack"
        class="hina-search-heading-logo"
      />
    </h1>
    <form
      class="hina-search-form"
      @submit.prevent="search()"
    >
      <div class="hina-search-form-inner">
        <input
          type="search"
          v-model="keyword"
          :placeholder="ls(dapLangGlobal.top.inputSearchKeyword)"
          class="hina-search-form-input"
        />
        <div
          class="hina-search-form-buttons"
        >
          <UiButtonIcon
            icon="mdi/magnify"
            class="hina-search-form-submit"
            inner-class="hina-search-form-submit-inner"
            :arialabel="ls(dapLangGlobal.search)"
            icon-size="size-4"
            type="submit"
          />
          <UiButtonIcon
            icon="mdi/image-search"
            class="hina-search-form-submit"
            inner-class="hina-search-form-submit-inner"
            :arialabel="ls(dapLangGlobal.imageSearch)"
            icon-size="size-4"
            @click.stop="searchImageWithin()"
          />
        </div>
      </div>
      <div class="hina-search-form-target">
        <BaseToggleSwitch
          v-model="hasContent"
          arialabel="検索対象"
          label-position="left"
        >
          {{ ls(dapLangGlobal.imageOnly) }}
        </BaseToggleSwitch>
      </div>
      <p class="image-toggle-note">{{ ls(dapLangGlobal.imageOnlyDescription) }}</p>
    </form>

    <div class="about-hinagiku">
      <UiButtonLinkIcon
        class="about-hinagiku-link"
        to="/about"
      >
        <BaseIcon
          name="interface/beginner"
          size="size-5"
        />
        {{ lst(dapLangGlobal.top.aboutHinagiku, ls(dapLangGlobal.serviceName)) }}
      </UiButtonLinkIcon>
    </div>
  </BaseContainer>

  <ClientOnly>
    <template v-for="(data, i) in topPage?.parts" :key="i">
      <UiTopHinaSectionRender
        :curation-part="data"
        :class="{
          'news-container': isNewsSection(data),
        }"
        class="hina-top-renderer"
      >
        <template #default v-if="isNewsSection(data)">
          <UiNewsList
            :data="news"
          />
        </template>
      </UiTopHinaSectionRender>
    </template>
  </ClientOnly>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'

const { toListElement } = useListElement()
const { contentImageUrl } = useContent()

definePageMeta({
  disableDefaultMargin: true,
  qsearch: true,
})

const { getPageAndCache } = usePage()
const { page, meta } = await getPageAndCache()
useHead(meta)

const dataStore = useDataStore()
const { langNavigateTo, ls, lss, lst, ldate, langPath } = useI18n()
const router = useRouter()

const keyword = ref('')
const hasContent = ref(false)
const search = () => {
  langNavigateTo(`/csearch/${systemCrossSearchId()}${toSearchQueryString({
    csid: systemCrossSearchId(),
    keyword: split(keyword.value),
    filter: {
      caccess: hasContent.value ? ['+internet'] : undefined
    }
  })}`)
}

const searchActive: Ref<boolean> = ref(false)
const searchImageWithin = () => {
  searchActive.value = false
  router.push(langPath(`/csearch/${systemImageSearchId()}${toSearchQueryString({
    csid: systemImageSearchId(),
    keyword: split(keyword.value)
  })}`))
}

const topPage = computed((): Curation => {
  if (import.meta.client) {
    return dataStore.curation('jps-top')
  }
})

const images = computed((): CurationPartDefinition[] => {
  if (!topPage.value || topPage.value.parts.length <= 0) return []
  let list = clone(topPage.value.parts[0].parts)
  shuffle(list)
  return list
})

const imageListElements = computed((): ListElement[] => {
  return images.value.map((i) => toListElement(i))
})

const imageURLs = computed(() => {
  return images.value.map((i) =>
    contentImageUrl(i.image?.contentId, i.image?.imageId)
  )
})

const zbgIndex = ref(-1)

const currentImageElement = computed(() => {
  if (zbgIndex.value == -1) {
    if (imageListElements.value.length === 0) {
      return null
    } else {
      zbgIndex.value = 0
    }
  }
  return imageListElements.value[zbgIndex.value]
})

const currentImageDb = computed<string>(() => {
  if (currentImageElement.value.type === 'item'
    && currentImageElement.value.data?.common?.database) {
    return currentImageElement.value.data.common.database
  } else {
    return null
  }
})

const isNewsSection = (curationPart: CurationPartDefinition): boolean => {
  if (!curationPart) {
    return false
  } else {
    return curationPart.type === 'jps-curation-section' && curationPart.title?.ja === 'お知らせ'
  }
}

//News
const news = computed((): Page[] => {
  if (page?.pageParams['news']) {
    const newsPath = page.pageParams['news'].split(',')
    if (newsPath) {
      return [
        dataStore.get('page', newsPath[0]),
        dataStore.get('page', newsPath[1]),
        dataStore.get('page', newsPath[2])
      ].filter(v => v)
    }
  }
  return null
})
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

// ひなぎくトップ用リセットスタイル
.hina-top-section,
:deep(.hina-top-section) {
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0;
  }
}

// UiTopHinaSectionRender.vue との共通スタイル
.hina-top-section,
:deep(.hina-top-section) {
  display: flex;
  flex-direction: column;
  gap: 1rem 0;
  padding-top: 2.5rem;
  padding-bottom: 2.5rem;

  @include is-mobile {
    padding-top: 1.5rem;
    padding-bottom: 1.5rem;
  }

  .hina-top-section-description {
    color: $hina-text-sub;
    font-size: $size-7;
  }

  .hina-more-block {
    display: flex;
    justify-content: flex-end;
  }

  .hina-more-block-text {
    font-weight: 500;
  }
}

// カルーセルのテキストパネル
.jps-top {
  .jps-top-slide-container {
    padding-bottom: 0;
    position: relative;
    display: block;

    @include is-tablet() {
      width: 100%;
      position: relative;
    }
    @include is-mobile() {
      width: 100%;
      position: relative;
    }
  }
  .jps-top-slide-content {
    cursor: pointer;
    height: 100%;
  }
  .top-slide-title-layout {
    width: 30rem;
    max-height: 70%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 0.8rem;

    @include is-mobile() {
      width: calc(100% - 2rem);
    }
  }

  .top-slide-title-position {
    position: absolute;
    right: 2.5rem;
    bottom: 1rem;
    z-index: 52;

    @include is-mobile {
      left: 0;
      right: 0;
      margin: 0 auto;
      bottom: 2.625rem;
    }
  }

  .top-slide-title-styled {
    background-color: $white;
    box-shadow: 0 2px 12px 2px rgba(0, 0, 0, 0.10), 0 1px 6px 0 rgba(0, 0, 0, 0.30);
    border-radius: $global-radius;
    padding: 1.5rem;

    @include is-mobile {
      background-color: rgba(249, 249, 249, 0.9);
      padding: 1rem;
    }
  }

  .top-image-title {
    @include typo-header(bold, $jps-key-blue);
    margin: 0;
    cursor: pointer;
    height: 100%;
    display: inline;
    vertical-align: middle;
  }
  .top-image-description {
    @include typo-paragraph(normal, $black);
    @include is-mobile {
      // height: 1rem;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }
  .top-image-source {
    display: flex;
    flex-direction: column;
    a,
    span,
    button {
      line-height: 1.5;
      &:not(:last-child) {
        margin-right: 1rem;
      }
    }
  }
}

// 検索フォームのセクション
:deep(.hina-search) {
  justify-content: center;
}

.hina-search-heading {
  text-align: center;
}

.hina-search-heading-logo {
  width: auto;
  height: 100%;
  max-height: 8rem;

  :deep(img) {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center center;
  }
}

.hina-search-form {
    width: 100%;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    display: flex;
    flex-direction: column;
}
.hina-search-form-inner {
    width: 100%;
    max-width: 40rem;
    display: flex;
    gap: 0.5rem;
    border-radius: 9999px;
    border: 1px solid $jps-gray-600;
    padding-right: 1rem;

    @include focus-within();
}
.hina-search-form-input {
    height: 3rem;
    padding: 0.5rem 0 0.5rem 1.5rem;
    font-size: $size-6;
    flex-grow: 1;
}
.hina-search-form-buttons {
    display: flex;
    align-items: center;
    width: auto;
    gap: 0.25rem;
    flex: 0 0 auto;
}
.hina-search-form-submit {
  color: $black;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.hina-search-form-submit-inner {
    color: $jps-gray-700 !important;
}
.hina-search-form-target {
  display: flex;
  gap: 1.5rem;
}
.image-toggle-note {
  font-size: 0.75rem;
  text-align: center;
  margin: 0;
}

// 「ひなぎく」とは
.about-hinagiku {
    width: 100%;
    text-align: center;
}

.about-hinagiku-link {
    gap: 0.25rem;
    color: $jps-key-blue;
    font-size: $size-7;
}

// お知らせ
.news-container {
  background-color: $hina-background-cream;
  border: {
    width: 1px 0;
    style: solid;
    color: $hina-separator-dark;
  }
}
</style>